import {
  Clock01Icon as Clock,
  FavouriteIcon as Heart,
  AudioWave01Icon as AudioLines,
  CardExchange02Icon as CardExchange,
  Ticket01Icon as Ticket,
  Notification03Icon as Bell,
  DashboardSpeed02Icon as Home,
  MusicNote01Icon as Music,
  UserMultipleIcon as Users,
  Wallet01Icon as Wallet,
  Coins01Icon as HandCoins,
  ChartColumnIcon as BarChart3,
  GiftIcon as Gift,
} from "@hugeicons-pro/core-stroke-rounded";
import { GiftCardIcon } from "@hugeicons/core-free-icons";
import type { AppRole } from "@/types/supabase";

type HugeIcon = typeof Clock;

export interface AccountMenuNavItem {
  label: string;
  icon: HugeIcon;
  tab: string;
  badgeCount?: number;
}

export interface AccountMenuNavGroup {
  label: string;
  items: AccountMenuNavItem[];
}

export interface AccountMenuConfig {
  dashboardPath: string;
  groups: AccountMenuNavGroup[];
}

// Mirrors each role's real dashboard sidebar, in the same order and with
// the same icons, so the account menu never diverges from the dashboard
// it links into. Gating (locked/verified-only rows) lives on the
// dashboard itself; this menu is just quick navigation into it.
export function getAccountMenuConfig(
  role: AppRole | null | undefined,
  unreadNotifications = 0,
): AccountMenuConfig {
  if (role === "fan") {
    return {
      dashboardPath: "/dashboard/fan",
      groups: [
        {
          label: "Listening",
          items: [
            { label: "Overview", icon: Clock, tab: "overview" },
            { label: "My Favorites", icon: Heart, tab: "favorites" },
            { label: "Streaming History", icon: AudioLines, tab: "history" },
          ],
        },
        {
          label: "Gifting",
          items: [
            { label: "Send Gifts", icon: GiftCardIcon, tab: "gifting" },
            { label: "Transactions", icon: CardExchange, tab: "transactions" },
          ],
        },
        {
          label: "Account",
          items: [
            { label: "My Tickets", icon: Ticket, tab: "tickets" },
            {
              label: "Notifications",
              icon: Bell,
              tab: "notifications",
              badgeCount: unreadNotifications,
            },
          ],
        },
      ],
    };
  }

  if (role === "artist") {
    return {
      dashboardPath: "/dashboard/artist",
      groups: [
        {
          label: "Main",
          items: [
            { label: "Dashboard", icon: Home, tab: "dashboard" },
            { label: "My Tracks", icon: AudioLines, tab: "tracks" },
            { label: "Albums", icon: Music, tab: "albums" },
          ],
        },
        {
          label: "Money",
          items: [
            { label: "Sponsorships", icon: Users, tab: "sponsorships" },
            { label: "Wallet", icon: Wallet, tab: "wallet" },
            { label: "Earnings", icon: HandCoins, tab: "earnings" },
          ],
        },
        {
          label: "Insights",
          items: [
            { label: "Analytics", icon: BarChart3, tab: "analytics" },
            {
              label: "Notifications",
              icon: Bell,
              tab: "notifications",
              badgeCount: unreadNotifications,
            },
          ],
        },
      ],
    };
  }

  if (role === "sponsor") {
    return {
      dashboardPath: "/dashboard/sponsor",
      groups: [
        {
          label: "Main",
          items: [
            { label: "Dashboard", icon: Home, tab: "dashboard" },
            { label: "Tracks", icon: Music, tab: "tracks" },
            { label: "Requests", icon: Users, tab: "requests" },
          ],
        },
        {
          label: "Money",
          items: [
            { label: "Wallet", icon: Wallet, tab: "wallet" },
            { label: "Gifting", icon: Gift, tab: "gifting" },
          ],
        },
        {
          label: "Insights",
          items: [
            { label: "Analytics", icon: BarChart3, tab: "analytics" },
          ],
        },
      ],
    };
  }

  return {
    dashboardPath: "/dashboard",
    groups: [
      {
        label: "Account",
        items: [{ label: "Dashboard", icon: Home, tab: "" }],
      },
    ],
  };
}
